import {
    DependencyList,
    EffectCallback,
    useEffect,
    useRef,
} from 'react';

/**
 * Хук `useDebouncedEffect` выполняет переданный эффект с задержкой (debounce).
 * Эффект будет вызван только после того, как зависимости не будут меняться в течение указанного времени.
 *
 * @param effect - Эффект, который нужно выполнить. Аналогичен эффекту в `useEffect`.
 * @param deps - Массив зависимостей, при изменении которых эффект должен быть перезапущен.
 *              Если не передан, эффект будет запускаться при каждом рендере (аналог `useEffect` без deps).
 * @param delay - Задержка в миллисекундах, перед выполнением эффекта. По умолчанию 0 (нет задержки).
 *
 * @example
 * Пример использования с задержкой 300 мс
 * useDebouncedEffect(
 *   () => {
 *     console.log('Выполняется с задержкой', inputValue);
 *   },
 *   [inputValue],
 *   300
 * );
 *
 * @example
 * Пример без задержки (аналогично обычному useEffect)
 * useDebouncedEffect(
 *   () => {
 *     console.log('Выполняется без задержки');
 *   }
 * );
 *
 * @remarks
 * Этот хук полезен для оптимизации производительности, когда нужно уменьшить количество
 * вызовов эффекта при частом изменении зависимостей (например, при вводе текста в поле).
 */
export const useDebouncedEffect = (effect: EffectCallback, deps?: DependencyList | undefined, delay?: number) => {
    const effectRef = useRef(effect);
    effectRef.current = effect;

    useEffect(() => {
        const handler = setTimeout(() => effectRef.current(), delay ?? 0);

        return () => {
            clearTimeout(handler);
        };
    }, deps);
};
